import React, { useEffect, useState } from 'react';
import { Copy } from 'lucide-react';
import { toast } from 'sonner';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { isMysteryShoppingSurvey } from '@/components/SurveyExperienceSetting';

const TEXTS = [
  ['heading', 'Heading', 'Welcome!'],
  ['intro', 'Introduction', 'Welcome to the … Customer Experience Survey.'],
  ['qr_title', 'QR section title', 'If your receipt has a QR code'],
  ['qr_text', 'QR section text', 'Tap Scan QR Code and scan the QR code printed on your receipt.'],
  ['code_title', 'Code section title', 'If your receipt has a Feedback Code / Survey ID'],
  ['code_text', 'Code section text', 'Enter the code printed on your receipt below.'],
];
const blank = { enabled: false, slug: '', host: '', ...Object.fromEntries(TEXTS.map(([k]) => [k, ''])) };

const LinkRow = ({ url, testid }) => (
  <div className="flex items-center gap-2 text-[11px] text-[#334155] bg-[#F8FAFC] rounded px-2 py-1.5">
    <span className="flex-1 truncate font-mono" data-testid={testid}>{url}</span>
    <button type="button" onClick={() => { navigator.clipboard?.writeText(url); toast.success('Link copied'); }} aria-label="Copy link">
      <Copy className="w-3.5 h-3.5 text-[#64748B]" />
    </button>
  </div>
);

// One public link for the whole survey; the branch comes from the validated receipt QR / code.
export const PublicEntrySetting = ({ survey, onSave }) => {
  const [form, setForm] = useState({ ...blank, ...(survey?.public_entry || {}) });
  useEffect(() => { setForm({ ...blank, ...(survey?.public_entry || {}) }); }, [survey?.public_entry]);
  if (isMysteryShoppingSurvey(survey)) return null;
  const accessOn = !!survey?.access_validation?.enabled;
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const slug = form.slug.trim().toLowerCase();
  const host = form.host.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/+$/, '');

  return (
    <div className="space-y-2" data-testid="settings-public-entry">
      <div className="flex items-center justify-between gap-3">
        <label className="text-xs font-semibold text-[#334155]">Public Entry Link</label>
        <Switch checked={!!form.enabled} disabled={!accessOn} onCheckedChange={(v) => set('enabled', v)} data-testid="settings-public-entry-switch" />
      </div>
      <p className="text-[11px] text-[#94A3B8]">
        {accessOn ? 'One link for every branch — the branch is found from the receipt QR or feedback code.' : 'Turn on Survey Access Validation to use a public entry link.'}
      </p>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[11px] text-[#64748B] block mb-1">Link name</label>
          <Input value={form.slug} onChange={(e) => set('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))} placeholder="e.g. my-brand" className="h-8 text-xs" data-testid="settings-public-entry-slug" />
        </div>
        <div>
          <label className="text-[11px] text-[#64748B] block mb-1">Custom domain (optional)</label>
          <Input value={form.host} onChange={(e) => set('host', e.target.value)} placeholder="e.g. survey.example.com" className="h-8 text-xs" data-testid="settings-public-entry-host" />
        </div>
      </div>
      {slug && <LinkRow url={`${window.location.origin}/go/${slug}`} testid="settings-public-entry-url" />}
      {host && <LinkRow url={`https://${host}`} testid="settings-public-entry-host-url" />}
      <Collapsible>
        <CollapsibleTrigger className="text-[11px] font-semibold text-[#1A3E6F] underline" data-testid="settings-public-entry-text-toggle">Access page text</CollapsibleTrigger>
        <CollapsibleContent className="space-y-2 pt-2">
          {TEXTS.map(([k, label, ph]) => (
            <div key={k}>
              <label className="text-[11px] text-[#64748B] block mb-1">{label}</label>
              {k === 'intro'
                ? <Textarea rows={3} value={form[k]} onChange={(e) => set(k, e.target.value)} placeholder={ph} className="text-xs" data-testid={`settings-public-entry-${k}`} />
                : <Input value={form[k]} onChange={(e) => set(k, e.target.value)} placeholder={ph} className="h-8 text-xs" data-testid={`settings-public-entry-${k}`} />}
            </div>
          ))}
          <p className="text-[10px] text-[#94A3B8]">Leave blank to use the default wording shown as the placeholder.</p>
        </CollapsibleContent>
      </Collapsible>
      <Button type="button" size="sm" variant="outline" onClick={() => onSave({ ...form, slug, host })} data-testid="settings-public-entry-save-btn">
        Save public link
      </Button>
    </div>
  );
};
